// T2: Incoming call detail page (queued call).
//
// Covers Stitch v2 designs `incoming_call_intercom` +
// `incoming_call_axn_pro_access_branding`. The branded variant uses
// the operator's saved logo + brand color via useBranding(); same
// component renders both.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { useIntercomCall, useIntercomCalls, statusLabel, statusTone } from '../lib/intercomCalls.js';
import { useBranding } from '../lib/branding.js';
import { ApiError } from '../lib/api.js';

export function IncomingCallPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { call, error, loading, refresh } = useIntercomCall(id);
  const { answer } = useIntercomCalls();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading || !id) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !call) {
    return (
      <p role="alert" className="text-error">
        <Icon name="error" /> {error ?? 'Call not found'}
      </p>
    );
  }

  async function onAnswer() {
    if (!id) return;
    setBusy(true);
    setActionErr(null);
    try {
      await answer(id);
      navigate(`/intercom/active/${id}`);
    } catch (e) {
      setActionErr(
        e instanceof ApiError && e.code === 'conflict'
          ? 'Another operator already answered this call.'
          : e instanceof ApiError
            ? e.message
            : 'Could not answer call',
      );
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  const brandColor = branding.brand_color ?? undefined;

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="flex items-center gap-3">
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt=""
            className="h-10 w-10 object-contain rounded border border-outline-variant"
          />
        ) : (
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center"
            style={brandColor ? { background: brandColor, color: '#fff' } : { background: 'var(--primary-container)' }}
          >
            <Icon name="call" />
          </div>
        )}
        <div>
          <h1 className="text-xl font-semibold" style={brandColor ? { color: brandColor } : undefined}>
            Incoming call
          </h1>
          <p className="text-sm text-on-surface-variant">
            Callbox {call.callbox_id.slice(0, 8)}
          </p>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="text-center space-y-3">
            <Badge tone={statusTone(call.status)}>{statusLabel(call.status)}</Badge>
            <p className="text-3xl font-semibold">Ringing…</p>
            <p className="text-sm text-on-surface-variant">
              Started {new Date(call.initiated_at).toLocaleTimeString()}
            </p>
          </div>

          {call.snapshot_ids.length > 0 ? (
            <div className="mt-4">
              <img
                src={`/api/v1/snapshots/${call.snapshot_ids[0]}/image`}
                alt="Callbox snapshot"
                className="w-full rounded border border-outline-variant"
              />
            </div>
          ) : null}

          {actionErr ? (
            <p role="alert" className="text-error mt-3">
              <Icon name="error" /> {actionErr}
            </p>
          ) : null}

          <div className="flex gap-2 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/intercom/ops')}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={onAnswer}
              disabled={busy || call.status !== 'queued'}
              style={brandColor && call.status === 'queued' ? { background: brandColor } : undefined}
              className="flex-1"
            >
              <Icon name="call" /> {busy ? 'Answering…' : 'Answer'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
